import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';

/**
 * Detects the family of errors browsers raise when a hashed chunk
 * referenced by an already-loaded script no longer exists on the
 * server — typical after a Jenkins deploy bumps every file hash while
 * a visitor is mid-session.
 */
export function isChunkLoadError(err: unknown): boolean {
  const message =
    err instanceof Error
      ? `${err.name ?? ''} ${err.message ?? ''}`
      : typeof err === 'string'
        ? err
        : String((err as { message?: unknown } | null)?.message ?? '');
  if (!message) return false;
  return /Failed to fetch dynamically imported module|ChunkLoadError|Loading (chunk|CSS chunk) \S+ failed|Importing a module script failed/i.test(
    message,
  );
}

const RELOAD_SENTINEL_KEY = 'skilledLandingChunkReloadAtV1';
/** Hard guard so a misbehaving chunk-load can't spin the browser. */
const MIN_RELOAD_INTERVAL_MS = 8_000;

/**
 * Force a one-shot page reload, fenced behind a sessionStorage marker
 * so a chunk error that survives the reload doesn't loop forever.
 */
export function reloadOnceForStaleChunk(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_SENTINEL_KEY) ?? '0');
    if (Number.isFinite(last) && Date.now() - last < MIN_RELOAD_INTERVAL_MS) {
      // Already reloaded recently — the chunk reference is genuinely
      // broken, not just stale. Let the error bubble up instead of
      // looping the page.
      console.error(
        '[ChunkLoadError] Skipping reload — already reloaded within window.',
      );
      return false;
    }
    sessionStorage.setItem(RELOAD_SENTINEL_KEY, String(Date.now()));
  } catch {
    /* sessionStorage unavailable (private mode / quota) — try reload anyway. */
  }
  try {
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Global Angular error handler. Mostly delegates to the default
 * behaviour, but intercepts stale-chunk failures (typical right after
 * a deploy bumps the file hashes) and forces one reload so the visitor
 * picks up the new bundle instead of seeing a blank route.
 */
@Injectable()
export class ChunkAwareErrorHandler implements ErrorHandler {
  private readonly zone = inject(NgZone);

  handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      this.zone.runOutsideAngular(() => reloadOnceForStaleChunk());
      return;
    }
    console.error(error);
  }
}
